import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontKeyboardEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  AccountAttributeUpdateAction,
  updateName,
} from '../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountExternalInfoModel, AccountModel} from '../../../models/api/accountModel';
import {CrmSyncStatusesEnum} from '../../../models/api/crmSyncModel';
import {EditInCrmButtonContainer} from '../../common/crm/buttons/editInCrmButtonContainer';
import {InlineEditingProps, useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from './accountDetailsContext';

/*
 * Types.
 */

interface AccountDetailsHeaderNameProps {
  account: AccountModel;
  crmSyncEditLink?: string;
  crmSyncStatus?: CrmSyncStatusesEnum;
  shouldDisableActions?: boolean;
  updateAccountAttributeAsync: (action: AccountAttributeUpdateAction) => Promise<void>;
}

/*
 * Styles.
 */

const StyledNameContainerDiv = styled.div`
  position: relative;
`;

const StyledAccountNameDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 1.4;

  ${ellipsis()};
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  margin: auto;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );
`;

const StyledIconButton = styled(IconButton)`
  margin-top: 0;
`;

const StyledBoxedInputContainerDiv = styled.div`
  width: 100%;
  position: relative;
  left: -6px;
`;

const StyledBoxedInputElement = styled(BoxedInputElement)`
  margin: 3px 6px 2px 6px;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const AccountDetailsHeaderName: FC<AccountDetailsHeaderNameProps> = (props) => {
  const {account, crmSyncEditLink, shouldDisableActions, crmSyncStatus, updateAccountAttributeAsync} = props;
  const {onTrackEvent} = useAccountDetailsContext();

  const [shouldShowControls, setShouldShowControls] = useState(false);
  const inlineEditingProps = useInlineEditing({
    currentAttributeValue: account.name,
    onSave: (newValue: string) => {
      if (!newValue) {
        return Promise.resolve();
      }

      onTrackEvent(
        buildAccountDetailsEditPayload(
          account.id,
          AccountDetailsEditActionsEnum.EDIT,
          AccountDetailsEditAttributesEnum.NAME,
        ),
      );
      const action = updateName(account.name, newValue);
      return updateAccountAttributeAsync(action);
    },
  });

  const showControls = () => {
    if (inlineEditingProps.isEditing || inlineEditingProps.isSaving || shouldDisableActions) {
      return;
    }

    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  const onEditClick = () => {
    hideControls();
    inlineEditingProps.onEditStart();
  };

  return (
    <StyledNameContainerDiv
      onMouseEnter={showControls}
      onMouseMove={showControls}
      onMouseLeave={hideControls}
    >
      {renderNameOrInput(account, inlineEditingProps)}
      {shouldShowControls && (
        <StyledControlsDiv>
          {renderEditButtonOrLinkToCrm(onEditClick, account.externalInfo, crmSyncEditLink, crmSyncStatus)}
        </StyledControlsDiv>
      )}
    </StyledNameContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderEditButtonOrLinkToCrm(
  onEditClick: () => void,
  externalInfo?: AccountExternalInfoModel,
  crmSyncEditLink?: string,
  crmSyncStatus?: CrmSyncStatusesEnum,
) {
  // if the account is not syncing with external crm or if we have disabled the sync, enable the edit button
  if (!crmSyncEditLink || crmSyncStatus === CrmSyncStatusesEnum.DISABLED) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconButton
            iconName="editSmall"
            color={theme.greys.shade70}
            label={<Trans id="account-details-account-header-name-edit">Edit</Trans>}
            onClick={onEditClick}
          />
        )}
      </UserThemeConsumer>
    );
  }
  if (externalInfo && crmSyncEditLink) {
    return <EditInCrmButtonContainer source={externalInfo.source} linkUrl={crmSyncEditLink} />;
  }
  return null;
}

function renderNameOrInput(account: AccountModel, inlineEditingProps: InlineEditingProps) {
  if (!inlineEditingProps.isEditing) {
    return <StyledAccountNameDiv>{account.name}</StyledAccountNameDiv>;
  }

  const onKeyDown = (event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        inlineEditingProps.onEditSave(inlineEditingProps.editedValue);
        break;

      case Key.Escape:
        event.preventDefault();
        inlineEditingProps.onEditCancel();
        break;

      default:
      // Do nothing.
    }
  };

  return (
    <StyledBoxedInputContainerDiv>
      <BoxedInput shouldAutoFocus noMargin>
        {(childProps) => (
          <StyledBoxedInputElement
            {...childProps}
            type={BoxedInputElementTypesEnum.TEXT}
            value={inlineEditingProps.editedValue}
            onChange={inlineEditingProps.onEdit}
            onBlur={() => inlineEditingProps.onEditSave(inlineEditingProps.editedValue)}
            onKeyDown={onKeyDown}
            size={VisualSizesEnum.LARGE}
          />
        )}
      </BoxedInput>
    </StyledBoxedInputContainerDiv>
  );
}
